import first from 'lodash/first.js';
import forEach from 'lodash/forEach.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import keyBy from 'lodash/keyBy.js';
import merge from 'lodash/merge.js';
import toArray from 'lodash/toArray.js';
import uniq from 'lodash/uniq.js';
import unset from 'lodash/unset.js';
import values from 'lodash/values.js';

import moment from '@/plugins/vue-moment.js';
import ActivitySubmission from '@/store/modules/activitysubmissions/ActivitySubmission.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';

const collectionHelpers = EdverumTable.collection('activity-submissions');

export default merge(Collection('ActivitySubmissions', ActivitySubmission), {
    state: {
        activity_submissions_report: null,
        sort: {
            attempt: null,
            created_at: null,
            updated_at: null,
        },
    },

    getters: {
        ...collectionHelpers.getters,

        all(state) {
            return () => {
                const items = { ...state };
                unset(items, 'loading');
                unset(items, 'sort');
                unset(items, 'filter');
                unset(items, 'paginate');
                unset(items, 'activity_submissions_report');

                return items;
            };
        },

        latest(state, getters) {
            return first(values(getters.all()).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
        },

        latestUserSubmission(state, getters, rootState) {
            return first(
                values(getters.all())
                    .filter(submission => get(submission, 'created_by') === rootState.Auth.user.id)
                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
            );
        },
    },

    actions: {
        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,
        clearSort: collectionHelpers.actions.clearSort,

        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder, payload }) => {
            const { getters } = context;

            if (has(payload, 'activityId')) {
                queryBuilder.raw({
                    'filter[activity_id]': payload.activityId,
                });
            }

            if (has(payload, 'createdBy')) {
                queryBuilder.raw({
                    'filter[created_by]': payload.createdBy,
                });
            }

            const includes = ['submittable', 'creator', 'attemptAuthorizations.user', 'attemptAuthorizations.creator'];

            if (payload.activityType === 'file-submission') {
                includes.push('submittable.media');
            }

            queryBuilder
                .setEndpoint('activity-submissions')
                .include(includes)
                .sort(getters.queryBuilderSorting)
                .raw({ per_page: 100 })
                .setPagination(getters.paginate);
        }),

        addActivitySubmission: collectionHelpers.actions.addModel(),

        updateActivitySubmission: collectionHelpers.actions.updateModel(),

        deleteActivitySubmission: collectionHelpers.actions.deleteModel(),

        async hydrateActivityGraderNavigation({ dispatch }, activityId) {
            const data = await dispatch('fetchAll', {
                filters: { key: 'id', value: activityId },
                endpoint: 'ungraded-activity-submissions',
            }).then(resp => resp?.data?.data);

            if (!data) {
                throw new Error('No submission data received, notify support');
            }

            await dispatch('sortAllSubmissions', data);
        },

        async getSubmissionsReportData({ dispatch }, { courseId }) {
            const data = await dispatch('fetchAll', {
                filter: { key: 'course_id', value: courseId },
                endpoint: 'ungraded-activity-submissions',
            }).then(resp => resp?.data?.data);

            if (!data) return null;

            const ungradedActivities = data.filter(activity => {
                activity.submissions_count = activity.submissions.length;

                return activity.submissions.length > 0;
            });

            return dispatch('sortAllSubmissions', ungradedActivities);
        },

        async sortAllSubmissions({ commit, dispatch }, ungradedActivities) {
            const sorted = await Promise.all(
                ungradedActivities.map(activity => dispatch('sortActivitySubmissions', activity))
            );

            const keyed = keyBy(sorted, item => item.id);

            commit('HYDRATE_SUBMISSIONS_REPORT', keyed);

            return sorted;
        },

        sortActivitySubmissions(context, activity) {
            let userKeys = [];
            const sorted = {};

            activity.submissions.forEach(item => userKeys.push(item.creator.id));

            userKeys = uniq(userKeys);

            userKeys.forEach(key => {
                sorted[key] = {};
                sorted[key].submissions = [];

                activity.submissions.forEach(sub => {
                    if (Number(sub.creator.id) === Number(key)) {
                        merge(sorted[key], sub.creator);

                        sorted[key].submissions.push(sub);
                    }
                });

                /* eslint-disable no-param-reassign */
                forEach(sorted, user => {
                    user.submissions = user.submissions.sort(
                        (a, b) => moment(b.updated_at).valueOf() - moment(a.updated_at).valueOf()
                    );
                });
                /* eslint-disable no-param-reassign */
            });

            activity.submissions = toArray(sorted);

            return activity;
        },

        async getNextPage({ commit, dispatch }, activity) {
            const { data } = await dispatch('fetchAll', {
                filter: { key: 'activity_id', value: activity.id },
                include: ['creator', 'submittable', 'gradebookGrades'],
                appends: 'graded_at',
                raw: { page: activity.next_page },
                endpoint: 'activity-submissions',
            });

            const updatedActivitySubmissions = await dispatch('sortActivitySubmissions', data);

            commit('UPDATE_SUBMISSION_REPORT', {
                activityId: activity.id,
                submissions: updatedActivitySubmissions,
            });
        },
    },

    mutations: {
        ADD_MODEL(state, submissions) {
            submissions.forEach((submission, index) => {
                submission.order = state.length + index;

                state[submission.id] = submission;
            });
        },

        HYDRATE_SUBMISSIONS_REPORT(state, submissions) {
            state.activity_submissions_report = submissions;
        },

        UPDATE_SUBMISSION_REPORT(state, { activityId, submissions }) {
            state.activity_submissions_report[activityId] = submissions;
        },

        PURGE_SUBMISSIONS_REPORT(state) {
            state.activity_submissions_report = null;
        },

        UPDATE_MODEL: collectionHelpers.mutations.UPDATE_MODEL,
        DELETE_MODEL: collectionHelpers.mutations.DELETE_MODEL,
        CLEAR_SORTING: collectionHelpers.mutations.CLEAR_SORTING,
    },
});
